var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useState, useCallback } from 'react';
import { AIGeneralBlockCard } from '../../AIGeneralBlockCard';
import { Text } from '../../../atoms/Text/Text';
import { submitForm } from '../../../../features/AIChatWidget/services/chatApi';
import { FormFieldRenderer } from './components/FormFieldRenderer';
import { AdvancedSection } from './components/AdvancedSection';
import './ConversationalFormRendererStyles.scss';
const baseClass = 'qmb-ui-conversational-form-renderer';
export function ConversationalFormRenderer({ block }) {
    const form = block;
    const [formValues, setFormValues] = useState(() => {
        var _a;
        const initial = {};
        form.fields.forEach((f) => {
            var _a, _b, _c;
            initial[f.name] = (_c = (_b = (_a = form.defaults) === null || _a === void 0 ? void 0 : _a[f.name]) !== null && _b !== void 0 ? _b : f.value) !== null && _c !== void 0 ? _c : '';
        });
        (_a = form.advanced_fields) === null || _a === void 0 ? void 0 : _a.forEach((f) => {
            var _a, _b, _c;
            initial[f.name] = (_c = (_b = (_a = form.defaults) === null || _a === void 0 ? void 0 : _a[f.name]) !== null && _b !== void 0 ? _b : f.value) !== null && _c !== void 0 ? _c : '';
        });
        return initial;
    });
    const [errors, setErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleFieldChange = useCallback((name, value) => {
        setFormValues((prev) => (Object.assign(Object.assign({}, prev), { [name]: value })));
        setErrors((prev) => {
            const next = Object.assign({}, prev);
            delete next[name];
            return next;
        });
    }, []);
    const validateField = useCallback((field, value) => {
        var _a, _b, _c;
        if (field.required && (!value || (typeof value === 'string' && !value.trim()))) {
            return `${field.label} is required`;
        }
        if (((_a = field.validation) === null || _a === void 0 ? void 0 : _a.pattern) && typeof value === 'string') {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(value)) {
                return field.validation.message || `${field.label} is invalid`;
            }
        }
        if (((_b = field.validation) === null || _b === void 0 ? void 0 : _b.min) !== undefined && typeof value === 'number') {
            if (value < field.validation.min) {
                return `${field.label} must be at least ${field.validation.min}`;
            }
        }
        if (((_c = field.validation) === null || _c === void 0 ? void 0 : _c.max) !== undefined && typeof value === 'number') {
            if (value > field.validation.max) {
                return `${field.label} must be at most ${field.validation.max}`;
            }
        }
        return null;
    }, []);
    const validateAll = useCallback(() => {
        const allFields = [...form.fields, ...(form.advanced_fields || [])];
        const newErrors = {};
        allFields.forEach((field) => {
            const err = validateField(field, formValues[field.name]);
            if (err)
                newErrors[field.name] = err;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [form.fields, form.advanced_fields, formValues, validateField]);
    const handleBlur = useCallback((field) => {
        const err = validateField(field, formValues[field.name]);
        if (err) {
            setErrors((prev) => (Object.assign(Object.assign({}, prev), { [field.name]: err })));
        }
    }, [formValues, validateField]);
    const handleSubmit = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!validateAll())
            return;
        setIsSubmitting(true);
        try {
            const threadId = ((_a = block.metadata) === null || _a === void 0 ? void 0 : _a.thread_id) || '';
            yield submitForm(threadId, {
                form_values: formValues,
                submit_action: form.submit_action
            });
            setIsSubmitted(true);
        }
        catch (err) {
            console.error('[ConversationalFormRenderer] Submit error:', err);
        }
        finally {
            setIsSubmitting(false);
        }
    }), [validateAll, formValues, form.submit_action, block.metadata]);
    const renderFields = (fields) => (React.createElement("div", { className: `${baseClass}__fields` }, fields.map((field) => (React.createElement("div", { key: field.name, onBlur: () => handleBlur(field) },
        React.createElement(FormFieldRenderer, { field: field, value: formValues[field.name], onChange: handleFieldChange, disabled: isSubmitted || isSubmitting, error: errors[field.name] }))))));
    const sections = [];
    if (form.title) {
        sections.push({
            label: undefined,
            content: (React.createElement("div", { className: `${baseClass}__header` },
                React.createElement(Text, { variant: "small", as: "h3" }, form.title),
                form.description && React.createElement(Text, { variant: "body" }, form.description)))
        });
    }
    const footerGroups = [
        {
            actions: [
                {
                    id: 'submit',
                    label: isSubmitting ? 'Submitting...' : form.submit_label,
                    variant: 'primary',
                    onClick: () => {
                        void handleSubmit();
                    },
                    disabled: isSubmitted || isSubmitting
                },
                {
                    id: 'cancel',
                    label: form.cancel_label,
                    variant: 'default',
                    onClick: () => {
                        setIsSubmitted(true);
                    },
                    disabled: isSubmitted || isSubmitting
                }
            ]
        }
    ];
    return (React.createElement(AIGeneralBlockCard, { className: baseClass, sections: sections, footerGroups: isSubmitted ? [] : footerGroups },
        React.createElement("div", { className: `${baseClass}__body` },
            renderFields(form.fields),
            form.show_advanced && form.advanced_fields && form.advanced_fields.length > 0 && (React.createElement(AdvancedSection, null, renderFields(form.advanced_fields))),
            isSubmitted && (React.createElement("div", { className: `${baseClass}__submitted` },
                React.createElement("i", { className: "fa-light fa-check-circle", "aria-hidden": "true" }),
                React.createElement(Text, { variant: "body" }, "Form submitted"))))));
}
